/**
 * Typed client for the backend.
 *
 * Same-origin by default (`API_BASE_URL === ''`) — unchanged from before the Vercel move: a
 * dev build served directly by the backend still has no base URL to configure and no CORS
 * surface to get wrong. Deployed to Vercel, the webapp is on its own origin
 * (`*.vercel.app`/a custom domain) and the backend is on `tariq-api.praise-me.com` (Phase 0),
 * so `TARIQ_API_BASE_URL` is baked in at build time (see build.mjs) and every request becomes
 * cross-origin — which is why `credentials` switches to `'include'` in that case: the cookie
 * auth path needs it, and the backend's CORS allow-list (server.ts) is what makes that safe.
 */
import type { OnboardingState } from './session.ts';

export interface AtsSupportEntry {
  platform: string;
  atsPlatform: string | null;
  status: 'full' | 'partial' | 'needs_human_submit' | 'unverified';
  note: string;
}

declare const __TARIQ_API_BASE_URL__: string;
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const API_BASE_URL = typeof __TARIQ_API_BASE_URL__ === 'string' ? __TARIQ_API_BASE_URL__ : '';

export interface SurfacedCandidate {
  candidateId: string;
  postingId: string;
  title: string;
  company: string | null;
  decision: 'apply' | 'watch' | 'dismiss';
  decidedBy: 'hard_filter' | 'agent_judgment' | 'user';
  reason: string;
  fitScore: number;
}

export interface CandidateSummary {
  candidateId: string;
  postingId: string;
  title: string;
  company: string | null;
  url: string;
  state: string;
  stateReason: string | null;
  decidedBy: string | null;
  fitScore: number | null;
  createdAt: string;
  approvedAt: string | null;
}

export interface HuntResponse {
  surfaced: SurfacedCandidate[];
  shortlisted: number;
  judged: number;
  skipped: Array<{ postingId: string; reason: string }>;
}

export interface SayResponse {
  acknowledgement: string;
  plan: Array<{ tool: string; purpose: string; candidateId: string | null }>;
  results: Array<{ tool: string; purpose: string; output: unknown; ok: boolean }>;
  reply: string;
}

export type DepthTier = 'deep' | 'deeper' | 'deepest';

/**
 * A pipeline stage for post-submission application tracking (Contacted, Interviewing, Offer, Rejected, etc).
 */
export interface ApplicationStage {
  id: string;
  name: string;
  category: 'active' | 'terminal_success' | 'terminal_failure';
  orderNr: number;
}

/**
 * A single transition in an application's stage history.
 */
export interface ApplicationTransition {
  id: string;
  previousStageId: string | null;
  newStageId: string;
  actorType: 'human' | 'system_agent';
  actorId: string;
  reasoning: string | null;
  createdAt: string;
}

/**
 * The fields the plain Profile form (Roles tab) reads and writes — a subset of the backend's
 * full `ProfileRow`, limited to what a user actually edits by hand today. Everything else on
 * the row (identity fields, references, etc.) still round-trips untouched through `PATCH
 * /profile` because that route is a true patch: an omitted key here is never sent.
 */
export interface ProfileSummary {
  targetRoles: string[];
  salaryFloor: number | null;
  salaryCurrency: string | null;
  workAuthorization: string | null;
  currentLocation: string | null;
  /** Where the candidate wants to work — distinct from `currentLocation` (see schemas.ts). */
  locations: string[];
  dealBreakers: string[];
}

export type ProfilePatch = Partial<Omit<ProfileSummary, 'targetRoles'>> & {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  linkedinUrl?: string;
  githubUrl?: string;
};

export interface ResumeUploadResult {
  resumeText: string;
  proposed: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    linkedinUrl?: string;
    githubUrl?: string;
    currentLocation?: string;
    targetRoles: string[];
  };
  dropped: string[];
}

/**
 * Errors are surfaced with the server's own message rather than a generic one. The
 * messages this backend produces are written to be read by the user — "no rules document"
 * and "no embedder configured" are different problems with different fixes, and
 * flattening both to "something went wrong" hides which one happened.
 */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    // 'include' cross-origin (Vercel webapp -> tariq-api.praise-me.com) so the session
    // cookie actually rides along; 'same-origin' (the default) when served by the backend
    // itself, which is the unchanged, no-CORS-needed dev path.
    credentials: API_BASE_URL ? 'include' : 'same-origin',
  });

  const text = await response.text();
  const parsed: unknown = text === '' ? {} : JSON.parse(text);

  if (!response.ok) {
    const message =
      typeof parsed === 'object' && parsed !== null && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `Request failed with ${response.status}`;
    throw new ApiError(response.status, message);
  }
  return parsed as T;
}

/** Where the browser goes to start Google sign-in (a full-page navigation, not a fetch). */
export const loginUrl = `${API_BASE_URL}/auth/google/login`;

export const api = {
  me: () => request<{ email: string; plan: 'free' | 'premium' | 'owner' }>('GET', '/auth/me'),

  logout: () => request<{ ok: true }>('POST', '/auth/logout', {}),

  startPairing: () => request<{ code: string; expiresAt: string }>('POST', '/auth/device/start', {}),

  say: (message: string, history: Array<{ role: 'user' | 'assistant'; content: string }>, tier: DepthTier) =>
    request<SayResponse>('POST', '/say', { message, history, tier }),

  hunt: () => request<HuntResponse>('POST', '/hunt', {}),

  candidates: (states?: string[]) =>
    request<{ candidates: CandidateSummary[] }>(
      'GET',
      states && states.length > 0 ? `/candidates?state=${states.join(',')}` : '/candidates',
    ),

  approve: (candidateId: string) => request<{ ok: true }>('POST', '/candidates/approve', { candidateId }),

  getRules: () => request<{ content: string | null }>('GET', '/profile/rules'),

  saveRules: (content: string) => request<{ ok: true }>('PUT', '/profile/rules', { content }),

  saveResume: (label: string, content: string) =>
    request<{ resumeVersionId: string; accepted: boolean }>('POST', '/profile/resume', {
      label,
      content,
      proposedBy: 'user',
    }),

  /** Reads the file, sends it as base64 JSON, and returns the extracted text + proposed fields. Saves nothing. */
  uploadResume: async (file: File) => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return request<ResumeUploadResult>('POST', '/profile/resume/upload', {
      filename: file.name,
      contentBase64: btoa(binary),
    });
  },

  getOnboarding: () => request<OnboardingState>('GET', '/onboarding'),

  getAtsSupport: () => request<{ entries: AtsSupportEntry[] }>('GET', '/ats-support'),

  getProfile: () => request<ProfileSummary>('GET', '/profile'),

  patchProfile: (patch: ProfilePatch) => request<ProfileSummary>('PATCH', '/profile', patch),

  // The plain-form equivalent of the `set_target_roles` chat tool — same schema, same write,
  // reached without spending a model turn on a trivial edit. REPLACES the whole list.
  saveTargetRoles: (targetRoles: string[]) =>
    request<ProfileSummary>('PATCH', '/profile/target-roles', { targetRoles }),

  // Application stage tracking (post-submission pipeline)
  getApplicationStages: () =>
    request<{ stages: ApplicationStage[] }>('GET', '/applications/stages'),

  moveApplicationStage: (candidateId: string, stageId: string, reasoning?: string) =>
    request<{ transitionId: string; newStage: ApplicationStage; updatedAt: string }>(
      'POST',
      '/applications/stage',
      { candidateId, stageId, reasoning },
    ),

  getApplicationTransitions: (candidateId: string) =>
    request<{ transitions: ApplicationTransition[]; currentStage: ApplicationStage | null }>(
      'GET',
      `/applications/transitions?candidateId=${encodeURIComponent(candidateId)}`,
    ),
};
